import { FunctionComponent, useEffect, useState } from "react";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import throttle from "lodash/throttle";
import {
  CategoryOfExpenseOptions,
  payment_request_default,
  PaymentModeOptions,
  PaymentRequestModel,
  po_payments_req_status,
} from "../../model/payment_request_model";
import { urls } from "../../../../utils/urls";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { fetchGet, fetchPost, fetchPut } from "../../../../service/api_client";
import LoadingWidget from "../../../../context_providers/modal/loader";
import moment from "moment";
import AttachmentViewer from "../../../../ui/new_components/common/attachment_viewer";
import { POType, PurchaseOrderModelV2 } from "../../model/purchase_order_model";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiHistory, mdiInformationOutline } from "@mdi/js";
import { currencyFormat } from "../../../../utils/currency_format";
import DrawerModal, {
  DrawerModalLoaction,
} from "../../../../ui/new_components/common/drawer_modal";
import {
  getProcurementAttachmentPRESignedURL,
  postProcurementAttachmentS3,
  updatePaymentRequestApi,
} from "../../purchase_order_create/service/purchase_order_repository";
import ActionHistoryAdvance from "../components/payment_request_advance_history_logs";
import {
  ModalAlignment,
  ModalType,
  useModal,
} from "../../../../context_providers/modal/modal_context";
import { POLineItemCard } from "../../purchase_order_create/components/po_line_item_card";
import { usePurchaseOrder } from "../../contexts/po-creation-context";
import { currencies } from "../../../accounts/models/party_model";
import { Project } from "../../../inventory/models/project_model";
import ModalDialog from "../../../../ui/new_components/common/modal";
import { ACTION, MODULE } from "../../../../utils/enums";
import { useAuth } from "../../../../context_providers/auth/authProvider";

interface PaymentRequestInvoiceViewProps {}

const PaymentRequestInvoiceScreen: FunctionComponent<
  PaymentRequestInvoiceViewProps
> = () => {
  const isUpdatePage = useRouteMatch(
    `${urls.po_advance_payment_request}/updateInvoice/:id`,
  );
  const { id } = useParams<{ id: string }>();
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/purchase-order/advance_payment_request`;
  const [showCreatePaymentRequestModal, setShowCreatePaymentRequestModal] =
    useState(false);
  const { showToast } = useToast();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [project, setProject] = useState<Project>();
  const { purchaseOrder, setPurchaseOrder, defaultProject, setDefaultProject } =
    usePurchaseOrder();
  const [payAmount, setPayAmount] = useState<number>(0);
  const [invoiceAmount, setInvoiceAmount] = useState<any>();
  const [data, setData] = useState<PaymentRequestModel>(
    payment_request_default || ({} as PaymentRequestModel),
  );
  const [
    showInvoicePaymentRequestWithdrwanModal,
    setShowInvoicePaymentRequestWithdrwanModal,
  ] = useState(false);
  const [invoicePaymentFileList, setInvoicePaymentFileList] = useState<File[]>(
    [],
  );
  const existingItems = purchaseOrder?.items || [];
  const { showModal } = useModal();
  const [procuremntRead, setProcurementRead] = useState(false);
  const { isAuthorised } = useAuth();

  useEffect(() => {
    const procuremnt_read_only = isAuthorised({
      module: MODULE.procurement_read_only,
      action: ACTION.READ,
    });

    setProcurementRead(procuremnt_read_only);
  }, [isAuthorised]);

  const handleInvoiceAmount = throttle(async (e: any) => {
    const amount = Number(e.target.value);
    setData({
      ...data,
      invoice_amount: amount,
    });
    await getInvoiceAmount(amount);
  }, 0);

  const getInvoiceAmount = throttle(async (amount: number) => {
    try {
      // Fetch the current total percentage from the backend
      const res = await fetchGet(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/purchase-order/advance_payment_request/get-percentage/${data.po_no}`,
      );

      const po_total =
        (purchaseOrder?.subtotal ?? 0) +
        (purchaseOrder?.cgst ?? 0) +
        (purchaseOrder?.sgst ?? 0) +
        (purchaseOrder?.igst ?? 0);

      setInvoiceAmount(
        (purchaseOrder?.total ?? 0) - res.totalAmount + payAmount,
      ); //include the current pr payment_amount as well.

      if (res.success) {
        const totalAmount = res.totalAmount;
        if (po_total - totalAmount + payAmount < amount) {
          showToast({
            type: ToastType.error,
            text: "Please enter correct amount",
          });
          return;
        }
        setData({
          ...data,
          invoice_amount: amount,
        });
      }
    } catch (error: any) {
      console.error("Error fetching percentage:", error);
      showToast({
        type: ToastType.error,
        text: error.message || "An unexpected error occurred.",
      });
    }
  }, 0);

  const showLog = () => {
    showModal({
      title: "Logs",
      type: ModalType.drawer,
      alignment: ModalAlignment.right,
      container: (id) => <ActionHistoryAdvance pr_id={data.id} data={data} />,
    });
  };

  useEffect(() => {
    // Fetch the data for the payment request (when editing or creating)
    if (id && isUpdatePage) {
      getData(id);
    }
  }, [data.id, invoiceAmount]);

  useEffect(() => {
    const fetchPurchaseOrder = async () => {
      if (data.po_no) {
        await fetchPurchaseOrderByPoNo(data.po_no);
      }
    };

    fetchPurchaseOrder();
  }, [data.po_no]);

  useEffect(() => {
    const fetchProjectData = async () => {
      if (purchaseOrder?.project_id && !project) {
        await getProjectData(purchaseOrder.project_id);
      }
    };

    fetchProjectData();
  }, [purchaseOrder?.project_id, project]);

  const resetForm = () => {
    setData(payment_request_default);
    setShowCreatePaymentRequestModal(false);
  };

  const handlePaymentAmount = (e: any) => {
    const amount = Number(e.target.value);
    if (!data?.invoice_amount) {
      showToast({
        type: ToastType.error,
        text: "Please enter invoice amount first",
      });
      return;
    }

    setData({
      ...data,
      payment_amount: amount,
    });
  };

  const submit = async () => {
    try {
      if (!data.payment_amount) {
        showToast({
          type: ToastType.error,
          text: "Please fill all the required details",
        });
        return;
      }

      setLoading(true);

      const res = data.id
        ? await fetchPut(`${url}/${data.id}`, data)
        : await fetchPost(url, data);

      if (res.success) {
        showToast({ type: ToastType.success, text: res.message });
        if (!data.id) {
          setData(payment_request_default);
        }
        setData((prevData) => ({
          ...prevData,
          ...res.data, // Update with the response data
        }));
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      console.error("Error submitting data:", error);
      showToast({ type: ToastType.error, text: "An error occurred" });
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseOrderByPoNo = async (
    poNo: string,
  ): Promise<PurchaseOrderModelV2 | null> => {
    try {
      const res = await fetchGet(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/purchase-order/${poNo}`,
      );
      if (res.success) {
        if (!res.data.vendor_currency) {
          res.data.vendor_currency = currencies.rupee;
          res.data.vendor_currency_to_inr = 1;
        }

        if (res.data.items === undefined || res.data.items === null) {
          res.data.items = [];
        }

        setPurchaseOrder({ ...res.data });
        if (res.data.items[0]?.project) {
          setDefaultProject(res.data.items[0].project);
        }

        return res.data as PurchaseOrderModelV2;
      } else {
        showToast({ type: ToastType.error, text: res.error });
        return null;
      }
    } catch (error) {
      showToast({ type: ToastType.error, text: (error as Error).message });
      return null;
    }
  };

  const getProjectData = async (id: string) => {
    setLoading(true);

    const res = await fetchGet(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/project/${id}`,
    );
    if (res.success) {
      setProject({ ...res.data });
    } else showToast({ type: ToastType.error, text: res.error });
    setLoading(false);
  };

  const handleCancelPaymentRequest = async () => {
    resetForm();
  };

  const PRStatus = async (status: string) => {
    if (status) {
      try {
        const res = await fetchPut(`${url}/${data.id}/payment_approval`, data);

        if (!res.success) {
          showToast({
            type: ToastType.error,
            text: "Failed to update PR status.",
          });
          return;
        }
        showToast({
          type: ToastType.success,
          text: "PR Status Updated.",
        });
        history.push(urls.po_payment_request_approval);
        // Your existing success/error logic...
      } catch (error) {
        console.error("Error while updating payment status:", error);
        showToast({
          type: ToastType.error,
          text: "Failed to update status. Try again later.",
        });
      }
    }
  };

  const handleUpdatePaymentRequest = async (e: any) => {
    e.preventDefault(); // Prevent form submission and page reload
    if (invoiceAmount < data.payment_amount) {
      showToast({
        type: ToastType.error,
        text:
          "Amount should not be greater than total amount: " + invoiceAmount,
      });
      return;
    }
    if (
      data.pr_status === "Approved" ||
      data.pr_status === "Rejected" ||
      data.pr_status === po_payments_req_status.withdrawn
    ) {
      showToast({
        type: ToastType.error,
        text: "The Approved/Rejected PR is not editable!!",
      });
      handleCancelPaymentRequest();
      return;
    }
    // Validate if payment amount and due date are provided
    if (
      !data.invoice_no ||
      !data.payment_amount ||
      !data.invoice_date ||
      !data.attachments
    ) {
      // if (!data.invoice_amount || !data.due_date) {
      return showToast({
        type: ToastType.error,
        text: "Please enter all values!",
      });
    }
    if (
      invoiceAmount < data.payment_amount ||
      (data?.invoice_amount ?? 0) < data?.payment_amount
    ) {
      showToast({
        type: ToastType.error,
        text:
          "Payment Amount cannot be greater than invoice amount!, available amount is: " +
          invoiceAmount,
      });
      return;
    }

    setLoading(true); // Start loading indicator

    try {
      // Get pre-signed URLs for file uploads
      const pre_signed_url_list = await getProcurementAttachmentPRESignedURL(
        invoicePaymentFileList,
      );
      if (!pre_signed_url_list) {
        showToast({
          type: ToastType.error,
          text: "Error in getting signed URL",
        });
        setLoading(false); // Stop loading on error
        return;
      }

      // Iterate over each file and upload it
      for (let i = 0; i < pre_signed_url_list.length; i++) {
        const [signed_url, file, attachment] = pre_signed_url_list[i];
        const res = await postProcurementAttachmentS3(
          signed_url,
          file,
          attachment,
        );
        if (!res) {
          showToast({
            type: ToastType.error,
            text: "Error in uploading attachments",
          });
          setLoading(false); // Stop loading on upload failure
          return;
        }
      }

      // Add new attachments to the state
      const newAttachments = pre_signed_url_list.map(
        ([, , attachment]) => attachment,
      );
      const updatedState = {
        ...data,
        attachments: [...data.attachments, ...newAttachments],
      };
      // Call the API to create the Advance Payment Request
      const create_payment_request_advance_payment_res =
        await updatePaymentRequestApi(updatedState.id, updatedState);
      setData(updatedState); // Update state with the new data

      if (create_payment_request_advance_payment_res.success) {
        showToast({
          type: ToastType.success,
          text: create_payment_request_advance_payment_res.message,
        });
      } else {
        showToast({
          type: ToastType.error,
          text: create_payment_request_advance_payment_res.error,
        });
      }
    } catch (error: any) {
      // Handle unexpected errors
      console.error(
        "Error in uploading attachments or creating payment request",
        error,
      );
      showToast({
        type: ToastType.error,
        text: error.message || "An unexpected error occurred",
      });
    } finally {
      // Ensure loading is stopped regardless of success or failure
      setLoading(false);
      setShowCreatePaymentRequestModal(false); // Close modal after processing
    }
  };

  const getData = async (id: string) => {
    try {
      setLoading(true);
      const res = await fetchGet(`${url}/${id}`);
      if (res.success) {
        const updatedData = { ...res.data, type: res.data.type }; // Update type property if necessary
        setData(updatedData); // Update state with merged data
        setPayAmount(updatedData.payment_amount);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({ type: ToastType.error, text: "An error occurred" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showInvoicePaymentRequestWithdrwanModal && (
        <ModalDialog
          id="payment_request_invoice_view_withdraw_modal"
          show={showInvoicePaymentRequestWithdrwanModal}
          title={"Invoice Payment Request Withrawal"}
          onClose={() => {
            setShowInvoicePaymentRequestWithdrwanModal(false);
            setData({
              ...data,
              pr_status: po_payments_req_status.pending,
            });
          }}
        >
          <div className="bg-white p-6 rounded-md shadow-lg w-96">
            <h2 className="text-md font-semibold mb-4">
              Are yous sure to withdraw the Payment Request?
            </h2>
            <div className="flex justify-between mt-4">
              <button
                id="payment_request_invoice_view_withdraw_cancel_btn"
                onClick={() => {
                  setShowInvoicePaymentRequestWithdrwanModal(false);
                  setData({
                    ...data,
                    pr_status: po_payments_req_status.pending,
                  });
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                No
              </button>
              <button
                id="payment_request_invoice_view_withdraw_confirm_btn"
                onClick={() => {
                  PRStatus(po_payments_req_status.withdrawn);
                }}
                className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
              >
                Yes
              </button>
            </div>
          </div>
        </ModalDialog>
      )}
      {showCreatePaymentRequestModal && (
        <DrawerModal
          id="payment_request_invoice_view_edit_drawer"
          show={showCreatePaymentRequestModal}
          title={"Update Payment Request"}
          onClose={resetForm}
          location={DrawerModalLoaction.right} // or whatever location you are using for the DrawerModal
        >
          <div className="flex flex-col gap-3 p-3">
            <div className="flex flex-col gap-5 w-auto">
              {/* First Row */}
              <div className="flex flex-row gap-5 w-full">
                <div className="flex flex-col gap-1 w-full">
                  <label htmlFor="" className="text-xs text-gray-500">
                    Invoice Number*
                  </label>
                  <input
                    id="payment_request_invoice_view_invoice_no"
                    type="text"
                    className="border px-2 py-1 rounded-md"
                    value={data.invoice_no}
                    onChange={(e) =>
                      setData({
                        ...data,
                        invoice_no: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="flex flex-col gap-1 w-full">
                  <label htmlFor="" className="text-xs text-gray-500">
                    Invoice Date
                  </label>
                  <input
                    id="payment_request_invoice_view_invoice_date"
                    type="date"
                    className="border px-7 py-1 rounded-md w-full"
                    value={
                      data?.invoice_date
                        ? moment(data?.invoice_date).format("YYYY-MM-DD")
                        : ""
                    }
                    onChange={(e) => {
                      const selectedDate = e.target.value; // Get the selected date string (YYYY-MM-DD)
                      const date = selectedDate
                        ? new Date(new Date(selectedDate).setHours(0, 0, 0, 0)) // Convert to a date object
                        : undefined; // Reset if no date is selected

                      // Update the paymentRequest state with the selected date
                      setData((pv) => ({
                        ...pv,
                        invoice_date: date!, // Save the selected date in state
                      }));
                    }}
                  />
                </div>
              </div>

              {/* Second Row */}
              <div className="flex flex-row gap-5 w-full">
                <div className="flex flex-col gap-1 w-full">
                  <label htmlFor="" className="text-xs text-gray-500">
                    Invoice Amount*
                  </label>
                  <input
                    id="payment_request_invoice_view_invoice_amount"
                    type="number"
                    className="border px-2 py-1 rounded-md w-full"
                    value={data.invoice_amount || ""}
                    onChange={handleInvoiceAmount}
                  />
                  <span className="font-light">
                    Available amount is:{invoiceAmount}
                  </span>
                </div>
                <div className="flex flex-col gap-1 w-full">
                  <label htmlFor="" className="text-xs text-gray-500">
                    Payment Amount*
                  </label>
                  <input
                    id="payment_request_invoice_view_payment_amount"
                    type="number"
                    placeholder="INR 0"
                    className="border px-2 py-1 rounded-md w-full"
                    value={data.payment_amount}
                    onChange={handlePaymentAmount}
                  />
                </div>
              </div>

              {/* Third Row */}
              <div className="flex flex-row gap-5 w-full">
                <div className="flex flex-col gap-1 w-full">
                  <label htmlFor="" className="text-xs text-gray-500">
                    Category of Expense
                  </label>
                  <select
                    id="payment_request_invoice_view_category_of_expense"
                    className="border px-2 py-1 rounded-md w-full"
                    value={data.category_of_expence} // Ensure default is "raw_material"
                    onChange={(e) =>
                      setData({
                        ...data,
                        category_of_expence: e.target.value,
                      })
                    }
                  >
                    <option value="" hidden>
                      Select
                    </option>
                    {Object.values(CategoryOfExpenseOptions).map((c, i) => (
                      <option value={c} key={i}>
                        {c}
                      </option>
                    ))}
                    {/* <option value="Raw Material">Raw Material</option>
                    <option value="Installation">Installation</option>
                    <option value="Installation and Earthing">
                      Installation and Earthing
                    </option>
                    <option value="Installation with material">
                      Installation with material
                    </option> */}
                    {/* Add more options as needed */}
                  </select>
                </div>

                <div className="flex flex-col gap-1 w-full">
                  <label htmlFor="" className="text-xs text-gray-500">
                    No. of Installations
                  </label>
                  <input
                    id="payment_request_invoice_view_no_of_installations"
                    type="number"
                    className="border px-2 py-1 rounded-md w-full"
                    value={data.no_of_intallations}
                    onChange={(e) =>
                      setData({
                        ...data,
                        no_of_intallations: +e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <div className="flex flex-row gap-5 w-full">
                <div className="flex flex-col gap-1 w-full">
                  <label htmlFor="" className="text-xs text-gray-500">
                    Due Date
                  </label>
                  <input
                    id="payment_request_invoice_view_due_date"
                    type="date"
                    className="border px-7 py-1 rounded-md w-full"
                    value={
                      data?.due_date
                        ? moment(data?.due_date).format("YYYY-MM-DD")
                        : ""
                    }
                    min={moment().format("YYYY-MM-DD")} // Prevent selection of past dates
                    onChange={(e) => {
                      const selectedDate = e.target.value; // Get the selected date string (YYYY-MM-DD)
                      const date = selectedDate
                        ? new Date(new Date(selectedDate).setHours(0, 0, 0, 0)) // Convert to a date object
                        : undefined; // Reset if no date is selected

                      // Update the paymentRequest state with the selected date
                      setData((pv) => ({
                        ...pv,
                        due_date: date!, // Save the selected date in state
                      }));
                    }}
                  />
                </div>

                <div className="flex flex-col gap-1 w-full">
                  <label htmlFor="" className="text-xs text-gray-500">
                    Payment Mode
                  </label>
                  <select
                    id="payment_request_invoice_view_payment_mode"
                    className="border px-2 py-1 rounded-md w-full"
                    value={data.payment_mode} // Ensure default is "raw_material"
                    onChange={(e) =>
                      setData({
                        ...data,
                        payment_mode: e.target.value,
                      })
                    }
                  >
                    <option value="" hidden>
                      Select
                    </option>
                    {Object.values(PaymentModeOptions).map((c, i) => (
                      <option value={c} key={i}>
                        {c}
                      </option>
                    ))}
                    {/* Add more options as needed */}
                  </select>
                </div>
              </div>
              <div className="flex flex-col gap-1 w-full">
                <label htmlFor="" className="text-xs text-gray-500">
                  Customer Location
                </label>
                <input
                  id="payment_request_invoice_view_customer_location"
                  type="text"
                  placeholder="Write address here.."
                  className="border px-2 py-1 rounded-md w-full"
                  value={data.customer_location}
                  onChange={(e) =>
                    setData({
                      ...data,
                      customer_location: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="" className="text-xs text-gray-500">
                Attach Files
              </label>

              <input
                id="payment_request_invoice_view_attachment_file_input"
                type="file"
                multiple
                className="border px-2 py-1 rounded-md"
                onChange={(e) => {
                  if (e.target.files) {
                    const newFiles = Array.from(e.target.files);
                    setInvoicePaymentFileList((prevFiles) => [
                      ...prevFiles,
                      ...newFiles,
                    ]);
                  }
                }}
              />
              {/* Attach Files Section */}
              <div className="w-full mt-2 mx-auto">
                {/* Render previously uploaded files (data.attachments) */}
                {data.attachments?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">
                      Previous Attachments:
                    </h4>
                    {data.attachments.map((attachment, index) => (
                      <ul
                        key={index}
                        className="py-2 m-2 flex justify-around rounded-md text-black border-2"
                      >
                        <div>
                          <li className="flex items-center gap-2">
                            {/* Check for file type and render accordingly */}
                            {attachment?.type === "Image" ||
                            attachment?.type === "CSV" ? (
                              <img
                                src={attachment?.url} // Using signed URL for image
                                alt="Attachment preview"
                                className="w-16 h-16 object-cover"
                              />
                            ) : attachment?.type === "application/pdf" ? (
                              <embed
                                src={attachment.url} // Using signed URL for PDF preview
                                type="application/pdf"
                                className="w-16 h-16"
                              />
                            ) : (
                              <span>{attachment.url}</span> // For other file types, display the file name or URL
                            )}
                          </li>
                        </div>
                        <div>
                          <button
                            id={`payment_request_invoice_view_attachment_${attachment?.id ?? index}_remove_btn`}
                            type="button"
                            className="text-red-500"
                            onClick={() => {
                              // Handle removal of the previous attachment
                              const updatedAttachments =
                                data.attachments.filter((_, i) => i !== index);
                              setData({
                                ...data,
                                attachments: updatedAttachments, // Update the data state with the new list
                              });
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </ul>
                    ))}
                  </div>
                )}

                {/* Render newly selected files (advancePaymentFileList) */}
                {invoicePaymentFileList.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">
                      Newly Selected Files:
                    </h4>
                    {invoicePaymentFileList.map((file, index) => (
                      <ul
                        key={index}
                        className="py-2 m-2 flex justify-around rounded-md text-black border-2"
                      >
                        <div>
                          <li className="flex items-center gap-2">
                            {/* Check for file type and render accordingly */}
                            {file.type.startsWith("image") ? (
                              <img
                                src={URL.createObjectURL(file)} // Using object URL for image preview
                                alt={file.name}
                                className="w-16 h-16 object-cover"
                              />
                            ) : file.type === "pdf" ? (
                              <embed
                                src={URL.createObjectURL(file)} // Using object URL for PDF preview
                                type="application/pdf"
                                className="w-16 h-16"
                              />
                            ) : (
                              <span>{file.name}</span> // For other file types, display the file name
                            )}
                          </li>
                        </div>
                        <div>
                          <button
                            id={`payment_request_invoice_view_new_file_${index}_remove_btn`}
                            type="button"
                            className="text-red-500"
                            onClick={() => {
                              // Handle removal of the newly selected attachment
                              setInvoicePaymentFileList((prevFiles) =>
                                prevFiles.filter(
                                  (_, currIndex) => currIndex !== index,
                                ),
                              );
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </ul>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* Additional Notes Text Area */}
            <div className="flex flex-col gap-1">
              <label htmlFor="" className="text-xs text-gray-500">
                Additional Notes
              </label>
              <textarea
                id="payment_request_invoice_view_additional_notes"
                className="border px-2 py-1 rounded-md"
                value={data.notes}
                onChange={(e) =>
                  setData({
                    ...data,
                    notes: e.target.value,
                  })
                }
                placeholder="Enter any additional notes here"
              />
            </div>
            <div className="flex flex-row gap-4 justify-between mt-5">
              <div className="align-center justify-center flex">
                <button
                  id="payment_request_invoice_view_edit_cancel_btn"
                  onClick={handleCancelPaymentRequest}
                  className="bg-white px-24 py-2 rounded-md text-black ml-auto border-2"
                >
                  Cancel
                </button>
              </div>
              {/* Create Button */}
              <div className="bg-red-400">
                <button
                  id="payment_request_invoice_view_edit_submit_btn"
                  onClick={handleUpdatePaymentRequest}
                  className="bg-myPrimaryColor px-24 py-2 rounded-md text-white ml-auto border-2"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </DrawerModal>
      )}
      <div className="flex items-center justify-between py-2 px-5 sticky top-0 bg-myBgcolor">
        <div className="flex flex-row">
          <button
            id="payment_request_invoice_view_back_btn"
            className="  flex  items-center p-2 mt-2 hover:text-gray-700  font-bold text-lg"
            onClick={() => {
              history.goBack();
            }}
          >
            <Icon path={mdiChevronLeft} className="h-6 w-6 font-bold"></Icon>
            Back
          </button>
          <div className="text-xl font-bold border-l-4 border-myPrimaryColor pl-2 mt-4">
            Invoice Payment Request <span className="text-sm">#{data.id}</span>
          </div>
        </div>
        {data.id && (
          <button
            id="payment_request_invoice_view_history_btn"
            title="Logs"
            // to={`${window.document.location.pathname}/log`}
            onClick={showLog}
            className="mx-2 rounded-full p-2 hover:scale-105 transform duration-150    bg-myPrimaryColor text-white   shadow cursor-pointer flex items-center justify-center"
          >
            <Icon path={mdiHistory} className="h-6 w-6  "></Icon>
          </button>
        )}
      </div>
      <div className="flex flex-col gap-4 mx-10 bg-white rounded-md p-5">
        <div className="flex flex-row justify-between items-center mx-5">
          <h3 className="text-xl text-black font-semibold">
            Invoice Payment Request
          </h3>
          {!procuremntRead && (
            <button
              id="payment_request_invoice_view_open_edit_btn"
              className="text-white bg-green-500 rounded-md border-2 p-2"
              onClick={() => {
                getData(id);
                setShowCreatePaymentRequestModal(true);
              }}
            >
              Update
            </button>
          )}
        </div>
      </div>
      <form onSubmit={submit} className=" px-6 py-5">
        <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-2 gap-4">
          {/* PR Details Section */}
          <div className="flex flex-col gap-4 ml-5 bg-white rounded-md p-5">
            <h3 className="text-xl text-black font-semibold">PR Details </h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              {/* Adjusted flex layout */}
              <div className="flex flex-col w-full sm:w-1/2">
                {/* Adjusted width for better responsiveness */}
                <label htmlFor="invoice_no">Invoice No.</label>
                <input
                  type="string"
                  disabled
                  className="bg-white"
                  id="invoice_no"
                  name="invoice_no"
                  value={data.invoice_no}
                />
              </div>
              <div className="flex flex-col w-full sm:w-1/2">
                <label htmlFor="payment_date">PO No.</label>
                <input
                  className="text-blue-500 bg-white"
                  type="string"
                  id="po_no"
                  name="po_no"
                  disabled
                  value={`#${data.po_no}`}
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div className="flex flex-col w-full sm:w-1/2">
                <label htmlFor="pay_amount">Payment Amount</label>
                <input
                  type="text"
                  id="pay_amount"
                  name="pay_amount"
                  disabled
                  className="bg-white text-green-500"
                  value={data.payment_amount}
                />
              </div>
              <div className="flex flex-col w-full sm:w-1/2">
                <label htmlFor="invoice_amount">Invoice Amount</label>
                <input
                  type="text"
                  id="invoice_amount"
                  name="invoice_amount"
                  disabled
                  className="bg-white text-green-500"
                  value={data.invoice_amount}
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div className="flex flex-col w-full sm:w-1/2">
                <label htmlFor="inv_date">Invoice date</label>
                <input
                  type="date"
                  id="inv_date"
                  name="inv_date"
                  className="bg-white"
                  disabled
                  readOnly
                  value={
                    moment(data?.invoice_date).format("YYYY-MM-DD") || "--"
                  }
                />
              </div>
              <div className="flex flex-col w-full sm:w-1/2">
                <label htmlFor="category">Category of Expense</label>
                <input
                  type="text"
                  id="category"
                  name="category"
                  className="bg-white"
                  disabled
                  value={data.category_of_expence}
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div className="flex flex-col w-full sm:w-1/2">
                <label htmlFor="created_on">Created On</label>
                <input
                  type="date"
                  id="created_date"
                  name="created_date"
                  className="bg-white"
                  disabled
                  readOnly
                  value={moment(data?.created_at).format("YYYY-MM-DD") || "--"}
                />
              </div>

              <div className="flex flex-col w-full sm:w-1/2">
                <label htmlFor="installations">No. of Installations</label>
                <input
                  type="text"
                  id="installations"
                  name="installations"
                  className="bg-white"
                  disabled
                  readOnly
                  value={data?.no_of_intallations}
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div className="flex flex-col">
                <label htmlFor="requested_by">Requested By</label>
                <input
                  type="text"
                  id="requested_by"
                  name="requested_by"
                  className="bg-white"
                  disabled
                  value={data.created_by_name}
                />
              </div>
              <div className="flex flex-col w-full sm:w-1/2">
                <label htmlFor="location">Customer Location</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  className="bg-white"
                  disabled
                  value={data.customer_location}
                />
              </div>
            </div>
            <hr />
            <div className="flex flex-col">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                className="bg-white border-2"
                value={data?.notes}
              />
            </div>
          </div>

          {/* Attachments Section */}
          <div className="flex flex-col gap-4 mr-5 bg-white rounded-md p-5">
            <h3 className="text-xl text-black font-semibold">
              Attachments({data?.attachments?.length})
            </h3>
            <div className="flex flex-col gap-4">
              {/* Scrollable container for the attachment viewer */}
              <div className="max-h-96 overflow-y-auto border-2 rounded-md">
                <AttachmentViewer attachments={data?.attachments} />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="flex flex-col gap-4 ml-5 my-5 bg-white rounded-md p-5">
            <div className="col-span-2 flex flex-col gap-2">
              <div className="">
                <label htmlFor="" className="text-xl text-black font-semibold ">
                  Project Details
                </label>
              </div>

              <div className=" text-sm mt-2">
                <div className="grid grid-cols-4">
                  <h5>Project name</h5>
                  <p className="col-span-3">:{defaultProject?.name}</p>
                </div>
                <div className="grid grid-cols-4">
                  <h5>Description</h5>
                  <p className="col-span-3">:{defaultProject?.description}</p>
                </div>
                <hr />
                <div className="grid grid-cols-4">
                  <h5>Amount :</h5>
                  <p className="col-span-3  text-right">
                    {currencyFormat(defaultProject?.totalBudget || 0)}
                  </p>
                </div>
                <div className="grid grid-cols-4">
                  <h5>Spent :</h5>
                  <p className="col-span-3  text-right">
                    {currencyFormat(defaultProject?.spent || 0)}
                  </p>
                </div>
                <hr />
                <div className="grid grid-cols-4">
                  <h5>Balance : </h5>
                  <p className="col-span-3 text-right">
                    {currencyFormat(
                      (defaultProject?.totalBudget || 0) -
                        (defaultProject?.spent || 0),
                    )}
                  </p>
                </div>

                <div className="flex gap-1 text-gray-500 text-xs mt-2 items-center">
                  <Icon path={mdiInformationOutline} size={0.8}></Icon>Note :
                  this balance is real-time ,Eg: if you create a po on x date
                  and balance is y then if you open the same po on z date
                  balance may be v.
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-col lg:flex-row justify-between lg:justify-between gap-4 my-5 mr-5 bg-white rounded-md p-5">
            <div className="flex flex-col gap-4">
              <div className="mt-5">
                <h3 className="text-xl text-black font-semibold">
                  Vendor Details
                </h3>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Changes here */}
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col">
                    <label htmlFor="vendor">Vendor</label>
                    <input
                      type="string"
                      disabled
                      className="bg-white"
                      id="vendor"
                      name="vendor"
                      value={purchaseOrder?.party?.name}
                    />
                  </div>
                  <div className="flex flex-col">
                    <label htmlFor="type">Type</label>
                    <input
                      className="text-blue-500 bg-white"
                      type="string"
                      id="type"
                      name="type"
                      disabled
                      value={purchaseOrder?.party?.type}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4 mt-5">
              <div>
                <h3 className="text-xl text-black font-semibold">
                  Shipping Profile
                </h3>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Changes here */}
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col">
                    <label htmlFor="address">Address</label>
                    <input
                      type="string"
                      disabled
                      className="bg-white"
                      id="address"
                      name="address"
                      value={`${purchaseOrder?.warehouse?.address?.city}, ${purchaseOrder?.warehouse?.address?.pincode}`}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="gap-4 m-5">
          <h3 className="text-sm font-bold">Items</h3>
          {(() => {
            const isJobWork = purchaseOrder?.type === POType["Job Work PO"];
            return (
              <div className="mt-2 flex flex-col gap-3">
                {(purchaseOrder?.items || []).map((item) => (
                  <POLineItemCard
                    key={item.id}
                    variant={isJobWork ? "jobwork" : "regular"}
                    mode="view"
                    currency={purchaseOrder.vendor_currency}
                    data={item}
                    existingItems={existingItems}
                    onSubmit={() => {}}
                  />
                ))}
              </div>
            );
          })()}
        </div>
        <div className="gap-4 m-5 bg-white p-5">
          <div className="flex justify-between font-bold ">
            <p>Total</p>
            <div className="">
              {currencyFormat(
                purchaseOrder?.total ?? 0,
                purchaseOrder?.vendor_currency?.short_name || "INR",
              )}
            </div>
          </div>
          <hr />
          <div className="flex justify-between font-bold ">
            <p>Paid</p>
            <div className="">
              {currencyFormat(
                purchaseOrder?.paid ?? 0,
                purchaseOrder?.vendor_currency?.short_name || "INR",
              )}
            </div>
          </div>
          <hr />
          <div className="flex justify-between font-bold ">
            <p>Balance</p>
            <div className="">
              {currencyFormat(
                (purchaseOrder?.total ?? 0) - (purchaseOrder?.paid || 0),
                purchaseOrder?.vendor_currency.short_name || "INR",
              )}
            </div>
          </div>
        </div>

        {loading && <LoadingWidget />}
      </form>
    </>
  );
};

export default PaymentRequestInvoiceScreen;
